import React, { useState } from "react";
import "./FeedbackPage.css";
import { useNavigate, Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Toast from "./Toast";
import API_BASE_URL from "../apiConfig";

export default function FeedbackPage() {
  const navigate = useNavigate();

  // Retrieve stored therapist profile from localStorage
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch (e) {
      return {};
    }
  })();

  const initialName =
    storedUser.name ||
    storedUser.fullname ||
    [storedUser.first_name, storedUser.last_name].filter(Boolean).join(" ") ||
    "";
  const initialEmail = storedUser.email || "";

  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const maxWords = 500;

  const countWords = (text) => {
    return text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setToast({ message: "Please enter your name", type: "error" });
      return;
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setToast({ message: "Please enter a valid email address", type: "error" });
      return;
    }

    if (!feedback.trim()) {
      setToast({ message: "Please enter your feedback message", type: "error" });
      return;
    }

    if (countWords(feedback) > maxWords) {
      setToast({ message: `Feedback message cannot exceed ${maxWords} words`, type: "error" });
      return;
    }

    setIsLoading(true);
    try {
      const localTime = new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

      const response = await fetch(`${API_BASE_URL}/therapist_api/send_feedback.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          feedback: feedback.trim(),
          submitted_at: localTime,
          timezone: localTimezone,
        }),
      });

      const data = await response.json();
      if (data.status) {
        setToast({
          message: data.message || "Feedback message sent successfully!",
          type: "success",
        });
        setFeedback("");
      } else {
        setToast({
          message: data.message || "Failed to send feedback message",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Feedback submission error:", err);
      setToast({
        message: "An error occurred while sending feedback message. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="feedback-header-row">
        <Link onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
          <FaArrowLeft className="back-icon" aria-hidden="true" />
          Previous page
        </Link>
        <div className="common-page-header">
          <h1 className="common-page-title">Feedback</h1>
          <p className="common-page-subtitle">
            Please fill out the form below with any feedback you may have
          </p>
        </div>
      </div>
      <div className="feedback-wrapper">
        <div className="feedback-container">
          <form className="feedback-form-group" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="feedback-name">Name</label>
              <input
                id="feedback-name"
                type="text"
                placeholder="Please enter your name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="feedback-email">Email</label>
              <input
                id="feedback-email"
                type="email"
                placeholder="Please enter your email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="feedback-feedback">Feedback Message</label>
              <textarea
                id="feedback-feedback"
                placeholder="Share your feedback message here ......."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={isLoading}
              />
              <div className="word-counter" role="status" aria-live="polite" aria-atomic="true">
                {countWords(feedback)}/{maxWords} max words
              </div>
            </div>

            <div className="feedback-submit-row">
              <button
                type="submit"
                className="submit-btn"
                aria-label="Send feedback message"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Feedback Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
